import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Image } from 'semantic-ui-react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/User';
import { StatusContext } from '../context/Status';
import {
  API,
  getCachedStatus,
  getRegistrationAvailability,
  isPasswordLoginEnabled,
  showError,
  showInfo,
  showSuccess,
  showWarning,
} from '../helpers';
import { onGitHubOAuthClicked, onLarkOAuthClicked } from './utils';
import AuthLayout from '../layout/AuthLayout';
import larkIcon from '../images/lark.svg';

const LoginForm = () => {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    wechat_verification_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [showWeChatLoginModal, setShowWeChatLoginModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [, userDispatch] = useContext(UserContext);
  const [statusState] = useContext(StatusContext);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || '/token';
  const status = useMemo(
    () => statusState?.status || getCachedStatus(),
    [statusState?.status]
  );
  const registration = getRegistrationAvailability(status);
  const passwordLoginEnabled = isPasswordLoginEnabled(status);
  const { username, password } = inputs;

  useEffect(() => {
    if (searchParams.get('expired')) {
      showError(t('messages.error.login_expired'));
    }
  }, [searchParams, t]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((current) => ({ ...current, [name]: value }));
  };

  const finishLogin = (data) => {
    userDispatch({ type: 'login', payload: data });
    localStorage.setItem('user', JSON.stringify(data));
    showSuccess(t('messages.success.login'));
    navigate(redirectTo);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!passwordLoginEnabled) {
      showInfo(t('auth.login.password_disabled'));
      return;
    }
    if (!username || !password || loading) return;
    setLoading(true);
    try {
      const res = await API.post('/api/user/login', { username, password });
      if (!res?.data) return;
      const { success, message, data } = res.data;
      if (success) {
        finishLogin(data);
        if (username === 'root' && password === '123456') {
          showWarning(t('messages.error.root_password'));
        }
      } else {
        showError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWeChatLogin = async (event) => {
    event?.preventDefault?.();
    if (!inputs.wechat_verification_code || wechatLoading) return;
    setWechatLoading(true);
    try {
      const res = await API.get(
        `/api/oauth/wechat?code=${inputs.wechat_verification_code}`
      );
      if (!res?.data) return;
      const { success, message, data } = res.data;
      if (success) {
        finishLogin(data);
        setShowWeChatLoginModal(false);
      } else {
        showError(message);
      }
    } finally {
      setWechatLoading(false);
    }
  };

  const hasOAuth =
    status.github_oauth || status.wechat_login || status.lark_client_id;

  return (
    <AuthLayout
      title={t('auth.login.title')}
      description={t('auth.login.subtitle')}
    >
      {passwordLoginEnabled ? (
        <form className='fvv-auth-form' onSubmit={handleSubmit}>
          <label className='fvv-field'>
            <span>{t('auth.login.username')}</span>
            <input
              className='fvv-input'
              name='username'
              value={username}
              onChange={handleChange}
              autoComplete='username'
              placeholder={t('auth.login.username')}
            />
          </label>
          <label className='fvv-field'>
            <span>{t('auth.login.password')}</span>
            <input
              className='fvv-input'
              name='password'
              type='password'
              value={password}
              onChange={handleChange}
              autoComplete='current-password'
              placeholder={t('auth.login.password')}
            />
          </label>
          <button
            className='fvv-solid-btn fvv-solid-btn--block'
            type='submit'
            disabled={loading || !username || !password}
          >
            {loading ? '...' : t('auth.login.button')}
          </button>
        </form>
      ) : (
        <p className='fvv-auth-footer'>{t('auth.login.password_disabled')}</p>
      )}

      <div className='fvv-auth-footer'>
        <div>
          {t('auth.login.forgot_password')}{' '}
          <Link to='/reset'>{t('auth.login.reset_password')}</Link>
        </div>
        <div style={{ marginTop: 8 }}>
          {t('auth.login.no_account')}{' '}
          <Link
            to='/register'
            onClick={(event) => {
              if (registration.enabled) return;
              event.preventDefault();
              showInfo(t(`auth.register.disabled_${registration.reason}`));
            }}
          >
            {t('auth.login.register')}
          </Link>
        </div>
      </div>

      {hasOAuth && (
        <>
          <div className='fvv-auth-divider'>
            <span>{t('auth.login.other_methods')}</span>
          </div>
          <div className='fvv-oauth-row'>
            {status.github_oauth && (
              <button
                type='button'
                className='fvv-oauth-btn'
                onClick={() => onGitHubOAuthClicked(status.github_client_id)}
              >
                GitHub
              </button>
            )}
            {status.wechat_login && (
              <button
                type='button'
                className='fvv-oauth-btn'
                onClick={() => setShowWeChatLoginModal(true)}
              >
                WeChat
              </button>
            )}
            {status.lark_client_id && (
              <button
                type='button'
                className='fvv-oauth-btn'
                onClick={() => onLarkOAuthClicked(status.lark_client_id)}
              >
                <img src={larkIcon} alt='' /> Lark
              </button>
            )}
          </div>
        </>
      )}

      <Modal
        onClose={() => setShowWeChatLoginModal(false)}
        open={showWeChatLoginModal}
        size='mini'
        closeIcon
      >
        <Modal.Header>{t('auth.login.wechat.title')}</Modal.Header>
        <Modal.Content>
          <Image src={status.wechat_qrcode} fluid alt={t('auth.login.wechat.qr_alt')} />
          <p className='fvv-auth-footer'>{t('auth.login.wechat.scan_tip')}</p>
          <form className='fvv-auth-form' onSubmit={handleWeChatLogin}>
            <label className='fvv-field'>
              <span>{t('auth.login.wechat.code_placeholder')}</span>
              <input
                className='fvv-input'
                name='wechat_verification_code'
                value={inputs.wechat_verification_code}
                onChange={handleChange}
                placeholder={t('auth.login.wechat.code_placeholder')}
              />
            </label>
            <button
              className='fvv-solid-btn fvv-solid-btn--block'
              type='submit'
              disabled={wechatLoading || !inputs.wechat_verification_code}
            >
              {wechatLoading ? '...' : t('auth.login.button')}
            </button>
          </form>
        </Modal.Content>
      </Modal>
    </AuthLayout>
  );
};

export default LoginForm;
